import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { createCustomer, updateCustomer, deleteCustomer } from '../services/customerService';
import { queryKeys } from '../constants/queryKeys';

/**
 * useCreateCustomer — Create a new customer
 *
 * Usage in AddCustomer.jsx:
 *   const createMutation = useCreateCustomer();
 *
 *   const onSubmit = (data) => {
 *     createMutation.mutate(data);
 *   };
 *
 *   // In the submit button:
 *   disabled={createMutation.isPending}
 *   {createMutation.isPending ? 'Saving...' : 'Submit'}
 */
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => createCustomer(data),

    onSuccess: () => {
      // Refresh the customer list after a successful create
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast.success('Customer created successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to create customer. Please try again.';
      toast.error(message);
    },
  });
};

/**
 * useUpdateCustomer — Update an existing customer
 *
 * Usage:
 *   const updateMutation = useUpdateCustomer();
 *   updateMutation.mutate({ id: 42, data: formData });
 */
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateCustomer(id, data),

    onSuccess: (_, variables) => {
      // Refresh both the list and the specific customer's detail
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast.success('Customer updated successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to update customer. Please try again.';
      toast.error(message);
    },
  });
};

/**
 * useDeleteCustomer — Delete a customer
 *
 * Usage:
 *   const deleteMutation = useDeleteCustomer();
 *   deleteMutation.mutate(customerId);
 */
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteCustomer(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast.success('Customer deleted successfully!');
    },

    onError: (error) => {
      const message = error?.response?.data?.message || 'Failed to delete customer. Please try again.';
      toast.error(message);
    },
  });
};
